import { requiresNativeLocalPreview } from '@src/services/LocalCanvasPreview';
import { useUserStoryUploadFlow } from '@src/stores-v2/StoreContext';

/**
 * PLAT-11228 (audio-first): object URL for the video file this session
 * uploaded for the story, or null when the session doesn't hold it (page
 * refresh, another device, multi-file upload). While the pipeline is still
 * producing renditions, player surfaces use this to play the local video
 * instead of showing a processing placeholder.
 */
export function useLocalPreviewVideoUrl(
  storyId?: string | null,
): string | null {
  const uploadFlowStore = useUserStoryUploadFlow();
  return uploadFlowStore.getLocalVideoUrl(storyId);
}

/**
 * The timeline seeks a second hidden video through the local upload to cut
 * filmstrip frames. That is cheap for the small files we can also send to the
 * renderer, but multi-gigabyte uploads can make Chromium index/decode the file
 * twice (native canvas preview + filmstrip extractor) and exhaust the renderer
 * process. Keep the live native player, but let oversized uploads use the
 * timeline's existing processing placeholders until server frames arrive.
 */
export function useLocalFramePreviewVideoUrl(
  storyId?: string | null,
): string | null {
  const uploadFlowStore = useUserStoryUploadFlow();
  if (!storyId) return null;
  const file = uploadFlowStore.localVideoFileRecord[storyId];
  if (!file || requiresNativeLocalPreview(file)) return null;
  return uploadFlowStore.getLocalVideoUrl(storyId);
}
